import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { ApiDigitalAuthPassword } from '../../services/auth/password.service';
import { Error } from '../../models/error.model';
import { GlobalSwal } from '../../styles/swal.style';
import { Message } from '../../models/message.model';
import { legendIconInvalid } from '../../utils/legend.data.util';
import { evaluateLegendRequiredIcon } from '../../scripts/validate.legend.script';
import { expressions } from '../../libraries/regular.expressions.library';
import { getError, getMessage } from '../../languages/translations/response';
import { errorWebHandler } from '../../scripts/error.web.handler.script';
import { authApiKey, authClear } from '../../scripts/authentication.script';

import AppLegend from '../../components/element/Legend';

export interface RecoverPageProps {};

let errorResponse: Error, messageResponse: Message;

const RecoverPage: React.FunctionComponent<RecoverPageProps> = props => {
  const {lang} = StateLanguage()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)
  const [loadIndicator, setLoadIndicator] = useState('off')
  const [email, setEmail] = useState({value: '', valid: false})

  const submitRecover = async (event: React.ChangeEvent <HTMLFormElement>) => {
    event.preventDefault()
    setLoadIndicator('on')

    if (email.valid) {
      await ApiDigitalAuthPassword.postRequest(authApiKey(), email.value).then( (response) => {
        if (response.status === 200) {
          messageResponse = response.data
          authClear()

          GlobalSwal.fire({
            title: getMessage(messageResponse.message, lang.code),
            text: lang.labels.messageWillBeSentYourEmailToRecoverAccount,
            icon: 'success',
            confirmButtonText: lang.labels.okGotIt
          }).then( () => {
            navigate('/auth/login', {replace: true})
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (!email.value.length) legendIconInvalid('input-email', 'legend-email-required')
        setLoadIndicator('off')
      })
    }
  }

  const handleChangeEmail = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setEmail({...email, value: event.target.value})
  }

  const validateEmail = () => {
    evaluateLegendRequiredIcon(expressions.email, email, setEmail, 'input-email', 'legend-email-valid', 'legend-email-required')
  }

  useEffect( () => {
    setMounted(true)

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-lg-500px bg-body rounded shadow-sm p-10 mx-auto">
      <form className="form w-100" noValidate onSubmit={submitRecover}>
        <div className="text-center mb-10">
          <h1 className="text-dark text-capitalize mb-3">{lang.labels.forgotPassword}</h1>
          <div className="text-muted fw-bold fs-4">{lang.labels.enterYourEmailToResetYourPassword}</div>
        </div>
        <div className="fv-row mb-10">
          <label className="form-label fs-6 fw-bolder text-dark">{lang.labels.email}</label>
          <input id="input-email" className="form-control form-control-lg" type="email" autoComplete="off" name="email" value={email.value} onChange={handleChangeEmail} onKeyUp={validateEmail} onBlur={validateEmail} />
          <AppLegend attribute={{check: email.valid, name: "email", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
        </div>
        <div className="d-flex flex-wrap justify-content-center">
          <button className="btn btn-lg btn-primary me-4" type="submit" data-kt-indicator={loadIndicator}>
            <span className="indicator-label fw-bolder">{lang.labels.submit}</span>
            <span className="indicator-progress">
              {lang.labels.pleaseWait}
              <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
            </span>
          </button>
          <Link to="/auth/login" className="btn btn-lg btn-secondary fw-bolder">{lang.labels.cancel}</Link>
        </div>
      </form>
    </div>
  )
};

export default RecoverPage;
